import { CollectorEvent, CollectorName, EventCollectorConfig, BatchResponse } from './types';

const USE_NO_CORS = false;

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

function jitter(): number {
  return Math.random() * 500;
}

function buildUrl(config: EventCollectorConfig, collector: CollectorName): string {
  return `${config.baseUrl}/v1/collect/${collector}/${config.sourceApp}/batch`;
}

function buildHeaders(config: EventCollectorConfig): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (config.apiKey) {
    headers['Authorization'] = `Bearer ${config.apiKey}`;
  }
  return headers;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function sendBatchNoCors(
  collector: CollectorName,
  events: CollectorEvent[],
  config: EventCollectorConfig
): Promise<BatchResponse> {
  const url = buildUrl(config, collector);

  await fetch(url, {
    method: 'POST',
    mode: 'no-cors',
    body: JSON.stringify({ events }),
  });

  return { accepted: events.length, total: events.length };
}

async function sendBatchCors(
  collector: CollectorName,
  events: CollectorEvent[],
  config: EventCollectorConfig
): Promise<BatchResponse> {
  const url = buildUrl(config, collector);
  const bodyStr = JSON.stringify({ events });
  const headers = buildHeaders(config);

  let lastError: Error = new Error('[EventCollector] unknown error');

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.requestTimeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: bodyStr,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 200) {
        return (await response.json()) as BatchResponse;
      }

      if (response.status === 204) {
        return { accepted: events.length, total: events.length };
      }

      if (response.status === 400) {
        console.warn('[EventCollector] batch rejected (400) — dropping');
        return { accepted: 0, total: events.length };
      }

      if (response.status === 404) {
        console.error('[EventCollector] unknown collector (404) — check baseUrl/sourceApp config');
        return { accepted: 0, total: events.length };
      }

      if (response.status === 429 || response.status >= 500) {
        const retryAfterHeader = response.headers.get('Retry-After');
        const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : null;

        if (attempt < RETRY_DELAYS_MS.length) {
          await sleep((retryAfterMs ?? RETRY_DELAYS_MS[attempt]) + jitter());
          continue;
        }
        throw new Error(`[EventCollector] giving up after ${attempt + 1} attempts (status ${response.status})`);
      }

      throw new Error(`[EventCollector] unexpected status ${response.status}`);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        lastError = new Error('[EventCollector] request timed out');
      } else {
        lastError = err instanceof Error ? err : new Error(String(err));
      }
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt] + jitter());
      }
    }
  }

  throw lastError;
}

export function sendBatch(
  collector: CollectorName,
  events: CollectorEvent[],
  config: EventCollectorConfig
): Promise<BatchResponse> {
  return USE_NO_CORS
    ? sendBatchNoCors(collector, events, config)
    : sendBatchCors(collector, events, config);
}

export function sendBeaconBatch(
  collector: CollectorName,
  events: CollectorEvent[],
  config: EventCollectorConfig
): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
  const url = buildUrl(config, collector);
  const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
  return navigator.sendBeacon(url, blob);
}

// Best-effort, fire-and-forget POST for page-teardown flushes when sendBeacon is
// unavailable. Honors USE_NO_CORS so the CORS/no-cors decision lives in one place,
// and uses keepalive so the request survives the document unloading.
export function sendBatchKeepalive(
  collector: CollectorName,
  events: CollectorEvent[],
  config: EventCollectorConfig
): void {
  const url = buildUrl(config, collector);
  fetch(url, {
    method: 'POST',
    ...(USE_NO_CORS ? { mode: 'no-cors' } : { headers: buildHeaders(config) }),
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {/* teardown — best effort */});
}
